import React, { memo, useCallback, useEffect, useRef } from 'react'

import { TagComponent as Tag } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import Modal from 'Pods/Shared/Modal/Modal'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { clearPendingGeneration } from 'Pods/ThreeDStudio/Services/pendingGenerationStorage'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import FreeBetaBody from 'Pods/ThreeDStudio/Shared/UsageBodies/FreeBetaBody/FreeBetaBody'
import HasCreditsBody from 'Pods/ThreeDStudio/Shared/UsageBodies/HasCreditsBody/HasCreditsBody'
import SubscribedBody from 'Pods/ThreeDStudio/Shared/UsageBodies/SubscribedBody/SubscribedBody'
import UpgradeBody from 'Pods/ThreeDStudio/Shared/UsageBodies/UpgradeBody/UpgradeBody'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { closeUsageModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import { STUDIO_ACCESS_TIER, USAGE_MODAL_REASON } from 'Pods/ThreeDStudio/Store/constants'

import useBreakpoint from 'Utils/useBreakpoint'

import EligibleForTrialBody from './Components/EligibleForTrialBody/EligibleForTrialBody'
import JoinWaitlistBody from './Components/JoinWaitlistBody/JoinWaitlistBody'
import WaitlistedBody from './Components/WaitlistedBody/WaitlistedBody'

const renderTag = (studioAccessTier, t) => {
  switch (studioAccessTier) {
    case STUDIO_ACCESS_TIER.IN_FREE_BETA:
      return <Tag className="usage-modal__tag">{t('usageModal.tags.freePlan')}</Tag>
    case STUDIO_ACCESS_TIER.SUBSCRIBED:
      return <Tag color="primary-24" className="usage-modal__tag">{t('usageModal.tags.subscription')}</Tag>
    default:
      return null
  }
}

function UsageModal() {
  const t = useTranslation()
  const isOpen = useThreeDStudioStore(state => state.ui.isUsageModalOpen)
  const usageModalReason = useThreeDStudioStore(state => state.ui.usageModalReason)
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs'
  const isMediumMobile = ['xs', 'sm'].includes(breakpoint)

  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const monthlyUsed = useThreeDStudioStore(state => state.subscription.monthlyGenerationsUsed)
  const monthlyAvailable = useThreeDStudioStore(state => state.subscription.monthlyGenerationsAvailable)
  const availableCredits = useThreeDStudioStore(state => state.generation.availableCredits) || 0

  // A drained trial can coexist with credits granted outside it (admin,
  // top-up); those users can still generate, so they get the credits view
  // instead of the exhausted-trial warning and its full progress bar.
  const isTrialDrainedWithCredits = monthlyAvailable > 0
    && monthlyUsed >= monthlyAvailable
    && availableCredits > 0

  // The impression fires only once a body is actually rendered: for the
  // paid-feature reason that is immediate, otherwise the tier-specific
  // branching below returns null until studioAccessTier resolves.
  const isVisible = isOpen
    && (usageModalReason === USAGE_MODAL_REASON.PAID_FEATURE || Boolean(studioAccessTier))
  const hasTrackedOpenRef = useRef(false)

  useEffect(() => {
    if (!isVisible) {
      hasTrackedOpenRef.current = false
      return
    }
    if (hasTrackedOpenRef.current) return
    hasTrackedOpenRef.current = true
    trackStudioEvent(STUDIO_EVENT.TRIAL_MODAL_VIEW, {
      tier: studioAccessTier || null,
      modal_reason: usageModalReason || 'quota',
    })
  }, [isVisible, studioAccessTier, usageModalReason])

  const handleClose = useCallback(() => {
    // Dismissing the modal (X / backdrop) abandons any pending post-auth generation, so a
    // later /ai visit does not silently replay it. The paid-subscription card redirects
    // instead of closing, so a real purchase never hits this path.
    clearPendingGeneration()
    closeUsageModal()
  }, [])

  if (!isOpen) {
    return null
  }

  // Opened from a locked control (Private mode, DCC Bridge, Export): show the
  // upgrade body with the advanced-features list for every tier that lacks
  // access, so this path must not depend on the tier-specific branching below
  // (which also returns null while the tier is unresolved).
  if (usageModalReason === USAGE_MODAL_REASON.PAID_FEATURE) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal"
        ariaLabel={t('usageModal.header.unlockAiGeneration')}
      >
        <UpgradeBody onClose={handleClose} showAdvancedFeaturesList />
      </Modal>
    )
  }

  if (!studioAccessTier) {
    return null
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width={isMobile ? 'small' : 'medium'}
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal usage-modal--eligible-for-trial"
        ariaLabel="Unlock generation"
      >
        <EligibleForTrialBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.MUST_JOIN_WAITLIST) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width={isMediumMobile ? 'small' : 'medium'}
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal usage-modal--eligible-for-trial"
        ariaLabel="Unlock generation"
      >
        <JoinWaitlistBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.WAITLISTED) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header={t('usageModal.header.betaAccessRequested')}
        customClass="usage-modal usage-modal--waitlisted"
        ariaLabel="Beta access requested"
      >
        <WaitlistedBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.NOT_SUBSCRIBED) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal"
        ariaLabel="Unlock generation"
      >
        <UpgradeBody onClose={handleClose} />
      </Modal>
    )
  }

  if (
    studioAccessTier !== STUDIO_ACCESS_TIER.IN_FREE_BETA
    && studioAccessTier !== STUDIO_ACCESS_TIER.HAS_CREDITS
    && studioAccessTier !== STUDIO_ACCESS_TIER.SUBSCRIBED
  ) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="small"
      header={t('usageModal.header.aiUsage')}
      headerAddon={renderTag(studioAccessTier, t)}
      customClass="usage-modal"
      ariaLabel="Usage"
    >
      {studioAccessTier === STUDIO_ACCESS_TIER.IN_FREE_BETA && (
        isTrialDrainedWithCredits
          ? <HasCreditsBody onClose={handleClose} />
          : <FreeBetaBody onClose={handleClose} />
      )}
      {studioAccessTier === STUDIO_ACCESS_TIER.HAS_CREDITS && (
        <HasCreditsBody onClose={handleClose} />
      )}
      {studioAccessTier === STUDIO_ACCESS_TIER.SUBSCRIBED && (
        <SubscribedBody onClose={handleClose} />
      )}
    </Modal>
  )
}

export default memo(UsageModal)
